import { Injectable, inject } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api/api.service';

export interface HealthStatus {
  status: 'healthy' | 'unhealthy' | 'degraded';
  timestamp: string;
  environment: string;
  region: string;
  version: string;
  uptime: number;
  services: {
    api: 'healthy' | 'unhealthy';
    database: 'healthy' | 'unhealthy';
    [key: string]: 'healthy' | 'unhealthy';
  };
  details?: {
    memory?: {
      heapUsed: number;
      heapTotal: number;
      usagePercent: number;
    };
    responseTime?: string;
    database_error?: string;
    memory_warning?: string;
    [key: string]: string | number | object | undefined;
  };
}

@Injectable({
  providedIn: 'root',
})
export class HealthCheckService {
  private readonly api = inject(ApiService);

  private readonly healthCheckUrl = `health`; /**
   * Performs a health check of the API
   * @returns Observable<HealthStatus> - The health status response
   */
  checkHealth(): Observable<HealthStatus> {
    // The authInterceptor will automatically add the Authorization header
    return this.api.get<HealthStatus>(this.healthCheckUrl).pipe(
      catchError((error) => {
        console.error('🚨 Health check failed:', error);
        const fallbackStatus: HealthStatus = {
          status: 'unhealthy',
          timestamp: new Date().toISOString(),
          environment: 'unknown',
          region: 'unknown',
          version: '1.0.0',
          uptime: 0,
          services: {
            api: 'unhealthy',
            database: 'unhealthy',
          },
          details: {
            error: error?.message || 'Health check request failed',
            responseTime: 'N/A',
          },
        };

        return throwError(() => fallbackStatus);
      })
    );
  }

  /**
   * Gets a simple health status indicator
   * @returns Observable<'online' | 'degraded' | 'offline'>
   */
  getHealthIndicator(): Observable<'online' | 'degraded' | 'offline'> {
    return this.checkHealth().pipe(
      map((health: HealthStatus): 'online' | 'degraded' | 'offline' => {
        switch (health.status) {
          case 'healthy':
            return 'online';
          case 'degraded':
            return 'degraded';
          case 'unhealthy':
          default:
            return 'offline';
        }
      }),
      catchError(() => of('offline' as const)) // Return offline if health check fails
    );
  }
}
